import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { API_URL } from '@/config/api';
import { authFetch } from '@/utils/auth-fetch';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { csvText, download } from './OrgContentDeliveryShared';

// How often to look again while the server is rebuilding the history.
export const REBUILD_POLL_MS = 15_000;

export const SERIES = [
  { key: 'first_generated', label: 'First generated', color: '#2563eb' },
  { key: 'generated', label: 'Generated, including regenerations', color: '#7c3aed' },
  { key: 'approved', label: 'Approved', color: '#d97706' },
  { key: 'translated', label: 'Fully translated', color: '#0891b2' },
  { key: 'published', label: 'Published', color: '#059669' },
] as const;
export type SeriesKey = typeof SERIES[number]['key'];
export type DailyPoint = { date: string } & Record<SeriesKey, number | null>;
export interface DailyActivity {
  start: string;
  end: string;
  generated_at: string;
  history_since: string | null;
  // #6808 follow-up: the server answers with its last finished result at once and rebuilds it in the background
  // when it is older than 10 minutes. Absent from an older backend.
  built_at?: string;
  stale?: boolean;
  rebuilding?: boolean;
  last_build_error?: { message: string; at: string } | null; // the last rebuild failed; these figures are older
  persistence_error?: { message: string; at: string; during: 'read' | 'write' } | null; // not kept for a restart
  // Compatibility release (rev. 3.1): this backend counts our records for every site and never sends
  // feed_coverage. A PREVIOUS backend (frontend-first or mixed-version deploy) can still answer
  // 'not_measured' with no points: that is unavailable, never zeros (codex-reviewer, #7936).
  sites: { site_id: string; site_name: string; points: DailyPoint[]; feed_coverage?: 'measured' | 'not_measured'; feed_unmeasured_sources?: string[] }[];
  notes: string[];
}

const DAY = 86_400_000;
export function validPeriod(start: string, end: string) {
  const day = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T00:00:00Z`) : new Date(NaN);
  const a = day(start); const b = day(end);
  return Number.isFinite(+a) && Number.isFinite(+b) && a.toISOString().slice(0, 10) === start
    && b.toISOString().slice(0, 10) === end && +b >= +a && +b - +a < 366 * DAY;
}
export const isActivityNotMeasured = (site: DailyActivity['sites'][number]) => site.feed_coverage === 'not_measured';
// A not-measured site (a previous backend's answer) is left out of the sums, and says so in its note. When every
// selected site is not measured there is nothing to sum: every value is unavailable (null), never 0.
export function aggregateDays(data: DailyActivity, selected: string[]): DailyPoint[] {
  const picked = data.sites.filter(site => selected.includes(site.site_id));
  const unavailable = picked.length > 0 && picked.every(isActivityNotMeasured);
  const days = new Map<string, DailyPoint>();
  for (let t = Date.parse(`${data.start}T00:00:00Z`); t <= Date.parse(`${data.end}T00:00:00Z`); t += DAY) {
    const date = new Date(t).toISOString().slice(0, 10);
    days.set(date, unavailable
      ? { date, first_generated: null, generated: null, approved: null, translated: null, published: null }
      : { date, first_generated: 0, generated: 0, approved: 0, translated: data.history_since && date < data.history_since.slice(0, 10) ? null : 0, published: 0 });
  }
  for (const site of picked) {
    if (isActivityNotMeasured(site)) continue;
    for (const point of site.points) {
      const total = days.get(point.date);
      if (total) for (const { key } of SERIES) {
        if (total[key] === null || point[key] === null) total[key] = null;
        else total[key] += point[key];
      }
    }
  }
  return [...days.values()];
}

// Group only the selected interval; Monday is a label, never an expanded query bound.
export function displayPoints(points: DailyPoint[], weekly: boolean, cumulative: boolean): DailyPoint[] {
  const grouped = new Map<string, DailyPoint>();
  for (const point of points) {
    const day = new Date(`${point.date}T00:00:00Z`);
    if (weekly) day.setUTCDate(day.getUTCDate() - (day.getUTCDay() + 6) % 7);
    const date = day.toISOString().slice(0, 10);
    const total = grouped.get(date) || {date, first_generated:0, generated:0, approved:0, translated:0, published:0};
    for (const {key} of SERIES) total[key] = total[key] === null || point[key] === null ? null : total[key]! + point[key]!;
    grouped.set(date, total);
  }
  const rows = [...grouped.values()];
  if (!cumulative) return rows;
  const sum: DailyPoint = {date:'', first_generated:0, generated:0, approved:0, translated:0, published:0};
  return rows.map(row => {
    for (const {key} of SERIES) sum[key] = sum[key] === null || row[key] === null ? null : sum[key]! + row[key]!;
    return {...sum, date:row.date};
  });
}
export function activityCsv(points: DailyPoint[], visible: SeriesKey[]): string {
  const lines = SERIES.filter(s => visible.includes(s.key));
  return csvText([['Date (UTC)', ...lines.map(s => s.label)], ...points.map(p => [p.date, ...lines.map(s => p[s.key] === null ? 'Unavailable' : String(p[s.key]))])]);
}

export default function DeliveryDailyActivity({ orgId }: { orgId: string }) {
  const [end, setEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [start, setStart] = useState(() => new Date(Date.now() - 29 * DAY).toISOString().slice(0, 10));
  const [pickedSites, setPickedSites] = useState<string[] | null>(null);
  const [visible, setVisible] = useState<SeriesKey[]>(SERIES.map(s => s.key));
  const [showTable, setShowTable] = useState(false);
  const [weekly, setWeekly] = useState(false);
  const [cumulative, setCumulative] = useState(false);
  const valid = validPeriod(start, end);
  const report = useQuery<DailyActivity>({
    queryKey: ['delivery-daily-activity', orgId, start, end],
    queryFn: async () => {
      const response = await authFetch(`${API_URL}/organizations/${orgId}/content-delivery/daily-activity?start=${start}&end=${end}`);
      if (!response.ok) {
        const body = await response.json();
        throw new Error(body.message || body.error || `Request failed (${response.status})`);
      }
      return response.json();
    },
    enabled: valid,
    staleTime: 10 * 60_000,
    // While the server rebuilds, look again soon so the new figures replace the old ones; else every 10 minutes.
    refetchInterval: (query) => (query.state.data?.rebuilding ? REBUILD_POLL_MS : 10 * 60_000),
  });
  const builtAt = report.data ? (report.data.built_at || report.data.generated_at) : null;
  const age = builtAt ? formatDistanceToNow(new Date(builtAt), { addSuffix: true }) : '';
  const sites = report.data?.sites || [];
  const selected = pickedSites === null ? sites.map(s => s.site_id) : pickedSites.filter(id => sites.some(s => s.site_id === id));
  const dailyPoints = useMemo(() => report.data ? aggregateDays(report.data, selected) : [], [report.data, selected.join(',')]);
  const points = useMemo(() => displayPoints(dailyPoints, weekly, cumulative), [dailyPoints, weekly, cumulative]);
  const shownSeries = SERIES.filter(s => visible.includes(s.key));
  const toggleSite = (id: string) => setPickedSites(selected.includes(id) ? selected.filter(s => s !== id) : [...selected, id]);
  const toggleLine = (key: SeriesKey) => setVisible(visible.includes(key) ? visible.filter(k => k !== key) : [...visible, key]);

  return <Card>
    <CardContent className="pt-6 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Product activity</h2>
          <p className="text-sm text-muted-foreground">Five product milestones · UTC</p>
        </div>
        <div className="flex gap-3">
          <label className="text-xs space-y-1">From<Input aria-label="Activity from" type="date" value={start} onChange={e => setStart(e.target.value)} /></label>
          <label className="text-xs space-y-1">To<Input aria-label="Activity to" type="date" value={end} onChange={e => setEnd(e.target.value)} /></label>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm">Period <select aria-label="Activity period" value={end === new Date().toISOString().slice(0,10) && [30,90,180,365].includes((Date.parse(end)-Date.parse(start))/DAY+1) ? String((Date.parse(end)-Date.parse(start))/DAY+1) : ''} onChange={e => { if (!e.target.value) return; const endDate = new Date(); setEnd(endDate.toISOString().slice(0,10)); setStart(new Date(+endDate - (Number(e.target.value)-1)*DAY).toISOString().slice(0,10)); }} className="border rounded p-2 bg-background">
          <option value="">Custom dates</option>{[30,90,180,365].map(n => <option key={n} value={n}>Last {n} days</option>)}
        </select></label>
        <label className="text-sm">Group <select aria-label="Activity grouping" value={weekly ? 'week' : 'day'} onChange={e => setWeekly(e.target.value === 'week')} className="border rounded p-2 bg-background"><option value="day">Day</option><option value="week">Week</option></select></label>
        <label className="text-sm">Show <select aria-label="Activity display" value={cumulative ? 'total' : 'per'} onChange={e => setCumulative(e.target.value === 'total')} className="border rounded p-2 bg-background"><option value="per">Per period</option><option value="total">Running total</option></select></label>
        <Button variant="outline" size="sm" disabled={!report.data || !valid || !selected.length || !visible.length} onClick={() => download(`product-activity-${start}-${end}.csv`, activityCsv(points, visible))}>Export activity CSV</Button>
      </div>
      {(weekly || cumulative) && <p className="text-xs text-muted-foreground">Sums of daily product counts within the selected dates. Products active on multiple days count on each day; these are not unique weekly or billing totals. Weeks are labelled by Monday. Unknown history stays unavailable.</p>}
      {!valid && <p role="alert" className="text-sm text-destructive">Choose a valid date range of up to 366 days, with From on or before To.</p>}
      {report.isLoading && valid && <p role="status">Loading daily activity…</p>}
      {report.error && <div role="alert" className="text-sm text-destructive">Could not load daily activity: {report.error.message} <Button variant="outline" size="sm" onClick={() => report.refetch()}>Retry</Button></div>}
      {report.data && valid && report.data.last_build_error && <p role="alert" className="text-sm text-destructive">The last update failed {formatDistanceToNow(new Date(report.data.last_build_error.at), { addSuffix: true })}: {report.data.last_build_error.message}. The figures below are from {age}.</p>}
      {report.data && valid && report.data.rebuilding && <p role="status" className="text-xs text-muted-foreground">Updating the figures. The ones below are from {age}.</p>}
      {report.data && valid && report.data.persistence_error && <p role="status" className="text-xs text-amber-700">These figures could not be saved for the server's next restart ({report.data.persistence_error.message}).</p>}
      {report.data && valid && !report.data.rebuilding && !report.data.last_build_error && builtAt && <p className="text-xs text-muted-foreground">Updated {age}.</p>}
      {report.data && valid && <>
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Sites</legend>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Button variant="ghost" size="sm" onClick={() => setPickedSites(null)}>All sites</Button>
            <Button variant="ghost" size="sm" onClick={() => setPickedSites([])}>Clear sites</Button>
            {sites.map(site => <label key={site.site_id} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={selected.includes(site.site_id)} onChange={() => toggleSite(site.site_id)} />{site.site_name}
            </label>)}
          </div>
        </fieldset>
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Lines</legend>
          <div className="flex flex-wrap gap-x-5 gap-y-2">{SERIES.map(series => <label key={series.key} className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={visible.includes(series.key)} onChange={() => toggleLine(series.key)} />
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: series.color }} aria-hidden="true" />{series.label}
          </label>)}</div>
        </fieldset>
        {!selected.length ? <p className="py-12 text-center text-muted-foreground">Select at least one site to see activity.</p>
          : !visible.length ? <p className="py-12 text-center text-muted-foreground">Select at least one line to see activity.</p>
          : <div role="img" aria-label="Daily product activity line chart. Exact values are available in the daily data table." className="h-[360px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={points} margin={{ top: 12, right: 20, bottom: 24, left: 12 }} accessibilityLayer>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tickFormatter={date => String(date).slice(5)} minTickGap={35} label={{ value: 'Date (UTC)', position: 'insideBottom', offset: -16 }} />
                <YAxis allowDecimals={false} width={70} label={{ value: weekly || cumulative ? 'Daily counts summed' : 'Products', angle: -90, position: 'insideLeft' }} />
                <Tooltip labelFormatter={date => `${date} (UTC)`} formatter={(value: number) => value.toLocaleString()} />
                {shownSeries.map(series => <Line key={series.key} type="linear" dataKey={series.key} name={series.label} stroke={series.color} strokeWidth={2} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />)}
              </LineChart>
            </ResponsiveContainer>
          </div>}
        {selected.length > 0 && visible.length > 0 && sites.some(s => selected.includes(s.site_id) && !isActivityNotMeasured(s)) && !points.some(p => shownSeries.some(s => (p[s.key] ?? 0) > 0)) && <p className="text-sm text-muted-foreground">No recorded activity for the selected sites, dates and lines.</p>}
        <p className="text-xs text-muted-foreground">Products with at least one PageMind generation, delivery or review event per day, for this site's canonical products, from our own records. Each product counts once per day per line. First generated is its first AEO generation; Generated includes that first generation and later regenerations. Sites with delivery disabled are included.</p>
        {sites.filter(s => isActivityNotMeasured(s) && selected.includes(s.site_id)).map(s => <p key={s.site_id} role="note" className="text-xs text-muted-foreground">{s.site_name}: activity not measured ({(s.feed_unmeasured_sources ?? []).join(', ')}): not available, not zero, and left out of these counts.</p>)}
        {report.data.notes.map(note => <p key={note} className="text-xs text-muted-foreground">{note}</p>)}
        <Button variant="outline" size="sm" onClick={() => setShowTable(!showTable)} aria-expanded={showTable}> {showTable ? 'Hide data' : weekly || cumulative ? 'Show chart data' : 'Show daily data'}</Button>
        {showTable && <div className="overflow-auto max-h-80"><table className="w-full text-sm tabular-nums">
          <caption className="text-left py-2">{weekly || cumulative ? 'Sums of daily counts for selected sites (UTC)' : 'Daily product counts for selected sites (UTC)'}</caption>
          <thead><tr><th className="text-left p-2">Date</th>{shownSeries.map(s => <th key={s.key} className="text-right p-2">{s.label}</th>)}</tr></thead>
          <tbody>{points.map(point => <tr key={point.date} className="border-t"><th scope="row" className="text-left p-2 font-normal">{point.date}</th>{shownSeries.map(s => <td key={s.key} className="text-right p-2">{point[s.key]?.toLocaleString() ?? '—'}</td>)}</tr>)}</tbody>
        </table></div>}
      </>}
    </CardContent>
  </Card>;
}
